// State Data console.
//
// One row per state, showing how far each one has actually got: can we pull a new year
// without a human, does the transform produce outputs, is there anything that would
// notice a release, and can the districts on that state see the result.
//
// The stages are deliberately shown as a pipeline rather than unrelated columns, because
// they gate each other -- a transform with no extractor is a state someone has to
// remember to feed by hand, and a registered client is the only stage a district actually
// experiences. A state is only "live" when all of them hold.
//
// The client half is a funnel: of the districts in this state, how many are enrolled at
// all, of those how many hold data, and of those how many can actually see it. Enrolment
// is first because it is the leakiest and the least visible -- nothing fails when a
// district was never added, so the gap only shows up if something counts it.
//
// Numbers come from a published snapshot, not a live scan: the scan walks the whole
// State Data Project in SharePoint plus every client container, which takes minutes.
// The generation time is shown rather than hidden -- stale numbers presented as current
// would be worse than numbers that say how old they are.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Alert, Box, Chip, CircularProgress, Collapse, IconButton, Paper, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import EnrolmentPanel, { failureDetail } from './EnrolmentPanel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import BlockIcon from '@mui/icons-material/Block';
import RefreshIcon from '@mui/icons-material/Refresh';
import { getStateStatus, getStatusRefreshJob, refreshStateStatus } from './api';

// The endpoint keys enrolment by full state name; the snapshot rows carry the abbrev.
const STATE_NAMES = {
    AL: 'alabama', CO: 'colorado', FL: 'florida', GA: 'georgia', KY: 'kentucky',
    LA: 'louisiana', MI: 'michigan', MS: 'mississippi', NC: 'north carolina',
    NY: 'new york', OH: 'ohio', PA: 'pennsylvania', SC: 'south carolina',
    TN: 'tennessee', TX: 'texas', WI: 'wisconsin',
};

const OK = '#2e7d32';
const NO = '#bdbdbd';
const WARN = '#ed6c02';
const BAD = '#c62828';

const STAGE_HELP = {
    extractor: 'Can a new year be pulled with no human involved?',
    transform: 'Segments with a crosswalk, and the merged outputs they produce.',
    scheduler: 'Something that would notice a release: a detection probe plus a poll window.',
    enrolled: 'Districts recorded in this state that have a State Of <State> data source '
        + 'at all. Enrolment is manual, so the rest is a standing gap nobody is told about.',
    clients: 'Of the enrolled clients, how many hold data in their container.',
    registered: 'Of the enrolled clients, how many have every output registered in Nexus — '
        + 'i.e. the district can actually see it.',
};

const StageIcon = ({ state }) => {
    if (state === 'yes') return <CheckCircleIcon sx={{ fontSize: 18, color: OK }} />;
    if (state === 'blocked') return <BlockIcon sx={{ fontSize: 18, color: WARN }} />;
    return <CancelIcon sx={{ fontSize: 18, color: NO }} />;
};

// A fraction is green only when complete, grey when nothing exists, amber in between —
// "3/4 registered" is a live problem, not a partial success.
const Fraction = ({ have, total }) => {
    const colour = total === 0 ? NO : have === total ? OK : have === 0 ? BAD : WARN;
    return (
        <Typography className="d-font" sx={{ fontSize: 13, color: colour, fontWeight: 600 }}>
            {have}/{total}
        </Typography>
    );
};

const extractorState = (r) => (r.extractor?.[0] ? 'yes'
    : (r.extractor?.[1] ? 'blocked' : 'no'));

const schedulerState = (r) => (r.scheduler?.probe && r.scheduler?.poll_cron ? 'yes' : 'no');

const enrolment = (r) => r.enrollment || {};
const enrolledCount = (r) => (enrolment(r).enrolled || []).length;
const eligibleCount = (r) => (enrolment(r).eligible || []).length;
const blockedCount = (r) => (enrolment(r).blocked || []).length;
const mismatchCount = (r) => (enrolment(r).state_mismatch || []).length;

const isLive = (r) => extractorState(r) === 'yes'
    && r.transform?.outputs > 0
    && schedulerState(r) === 'yes'
    && r.clients?.clients > 0
    && r.clients?.fully_registered === r.clients?.clients
    && eligibleCount(r) === 0 && blockedCount(r) === 0;

const POLL_MS = 10000;

// What the header says while a rescan runs: the job's last log line names the state or
// client being measured, so a multi-minute scan does not look hung.
export const scanProgress = (job) => {
    if (!job || job.phase === 'queued') return 'waiting for a pod';
    const last = (job.log || '').trim().split('\n').filter(Boolean).pop();
    return last || 'scanning';
};

const StateDataConsole = () => {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    // Which state's enrolment panel is open. One at a time: each open panel is a live
    // call, and the list is long enough that opening several would fire a burst.
    const [open, setOpen] = useState(null);
    // The rescan behind the refresh button: { phase: 'idle'|'running'|'error', detail }
    const [scan, setScan] = useState({ phase: 'idle' });
    const timer = useRef(null);

    // `quiet` keeps the table on screen (a reload after a rescan); the first load shows
    // the spinner.
    const load = useCallback(async (quiet = false) => {
        if (!quiet) setLoading(true);
        setError(null);
        try {
            setData(await getStateStatus());
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);
    useEffect(() => () => clearTimeout(timer.current), []);

    // The snapshot is only written by a scan, so "refresh" has to run one: start the job,
    // poll it, and re-read the snapshot once it has published.
    const rescan = async () => {
        setScan({ phase: 'running', detail: 'starting' });
        let job;
        try {
            job = await refreshStateStatus();
        } catch (e) {
            setScan({ phase: 'error', detail: e.message });
            return;
        }
        const tick = async () => {
            try {
                const j = await getStatusRefreshJob(job.job);
                if (j.phase === 'succeeded') {
                    setScan({ phase: 'idle' });
                    load(true);
                    return;
                }
                if (j.phase === 'failed') {
                    setScan({ phase: 'error', detail: failureDetail(j) });
                    return;
                }
                setScan({ phase: 'running', detail: scanProgress(j) });
            } catch (e) {
                setScan({ phase: 'running', detail: `checking… (${e.message})` });
            }
            timer.current = setTimeout(tick, POLL_MS);
        };
        timer.current = setTimeout(tick, POLL_MS);
    };

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
    }
    if (error) {
        return <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>;
    }

    const states = [...(data?.states || [])].sort((a, b) => {
        // Furthest along first, so the states that need attention are not buried under
        // the fourteen that have nothing.
        const score = (r) => (extractorState(r) === 'yes' ? 8 : 0)
            + (r.transform?.outputs > 0 ? 4 : 0)
            + (schedulerState(r) === 'yes' ? 2 : 0)
            + (r.clients?.clients > 0 ? 1 : 0);
        return score(b) - score(a) || a.abbrev.localeCompare(b.abbrev);
    });

    const liveCount = states.filter(isLive).length;
    const withClients = states.filter((r) => r.clients?.clients > 0);
    const unregistered = withClients.reduce(
        (n, r) => n + (r.clients.clients - r.clients.fully_registered), 0);
    // Only count unenrolled districts where the state is actually being published —
    // elsewhere there is no drop for them to be missing.
    const publishing = states.filter((r) => r.transform?.outputs > 0);
    const unenrolled = publishing.reduce((n, r) => n + eligibleCount(r), 0);
    const blockedTotal = publishing.reduce((n, r) => n + blockedCount(r), 0);

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <Typography className="d-font" sx={{ fontSize: 13, color: '#666' }}>
                    {liveCount} of {states.length} states fully live —
                    extractor, transform, scheduler and every client registered.
                </Typography>
                {unregistered > 0 && (
                    <Chip
                        size="small"
                        label={`${unregistered} client${unregistered === 1 ? '' : 's'} cannot see their data`}
                        sx={{ bgcolor: '#fdecea', color: BAD, fontSize: 12, height: 22 }}
                    />
                )}
                {unenrolled > 0 && (
                    <Tooltip title="Their state is published; they were never added to it.">
                        <Chip
                            size="small"
                            label={`${unenrolled} district${unenrolled === 1 ? '' : 's'} not enrolled`}
                            sx={{ bgcolor: '#fff4e5', color: WARN, fontSize: 12, height: 22 }}
                        />
                    </Tooltip>
                )}
                {blockedTotal > 0 && (
                    <Tooltip title="Cannot be enrolled until a storage container is provisioned.">
                        <Chip
                            size="small"
                            label={`${blockedTotal} blocked`}
                            sx={{ bgcolor: '#fdecea', color: BAD, fontSize: 12, height: 22 }}
                        />
                    </Tooltip>
                )}
                <Box sx={{ flex: 1 }} />
                <Typography className="d-font" sx={{ fontSize: 12, color: '#999' }}>
                    snapshot {data?.generated_at
                        ? new Date(data.generated_at).toLocaleString()
                        : 'unknown'}
                </Typography>
                {scan.phase === 'running' && (
                    <Typography className="d-font" sx={{ fontSize: 12, color: '#666' }}>
                        rescanning… {scan.detail}
                    </Typography>
                )}
                {scan.phase === 'error' && (
                    <Typography className="d-font" sx={{ fontSize: 12, color: BAD }}>
                        rescan failed — {scan.detail}
                    </Typography>
                )}
                <Tooltip title="Rescan every state and every district's enrolment, then republish the snapshot. Takes a few minutes; the page reloads when it has finished.">
                    <span>
                        <IconButton size="small" onClick={rescan} disabled={scan.phase === 'running'}
                            aria-label="rescan status" sx={{ p: 0.25 }}>
                            {scan.phase === 'running'
                                ? <CircularProgress size={14} />
                                : <RefreshIcon sx={{ fontSize: 16, color: '#777' }} />}
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>

            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid var(--color-border, #e0e0e0)' }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            {['State', 'Extractor', 'Transform', 'Scheduler', 'Enrolled', 'Holding data', 'Registered']
                                .map((h, i) => (
                                    <TableCell
                                        key={h}
                                        align={i >= 4 ? 'center' : 'left'}
                                        className="d-font"
                                        sx={{ fontSize: 12, fontWeight: 700, color: '#555' }}
                                    >
                                        {i === 0 ? h : (
                                            <Tooltip
                                                title={STAGE_HELP[
                                                    ['', 'extractor', 'transform', 'scheduler', 'enrolled', 'clients', 'registered'][i]
                                                ]}
                                            >
                                                <span style={{ borderBottom: '1px dotted #bbb', cursor: 'help' }}>{h}</span>
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {states.map((r) => {
                            const t = r.transform || {};
                            const c = r.clients || {};
                            const expandable = (enrolment(r).in_state || 0) > 0;
                            const isOpen = open === r.abbrev;
                            return (
                            <React.Fragment key={r.abbrev}>
                                <TableRow
                                    hover
                                    sx={{ cursor: expandable ? 'pointer' : 'default',
                                          '& > *': { borderBottom: isOpen ? 'unset' : undefined } }}
                                    onClick={() => expandable && setOpen(isOpen ? null : r.abbrev)}
                                >
                                    <TableCell sx={{ fontWeight: 700, fontSize: 13 }}>
                                        {expandable && (
                                            <IconButton size="small" sx={{ p: 0, mr: 0.5 }}>
                                                {isOpen
                                                    ? <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />
                                                    : <KeyboardArrowRightIcon sx={{ fontSize: 16 }} />}
                                            </IconButton>
                                        )}
                                        {r.abbrev}
                                        {isLive(r) && (
                                            <Chip label="live" size="small"
                                                sx={{ ml: 1, height: 18, fontSize: 10, bgcolor: '#e8f5e9', color: OK }} />
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Tooltip title={r.extractor?.[1] || 'no acquisition module'}>
                                            <Box sx={{ display: 'inline-flex' }}>
                                                <StageIcon state={extractorState(r)} />
                                            </Box>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell className="d-font" sx={{ fontSize: 12.5, color: '#444' }}>
                                        {t.segments
                                            ? `${t.with_crosswalk}/${t.segments} seg · ${t.outputs} out`
                                            : <span style={{ color: NO }}>empty folder</span>}
                                    </TableCell>
                                    <TableCell>
                                        <Tooltip title={r.scheduler?.poll_cron
                                            ? `poll_cron ${r.scheduler.poll_cron}${r.scheduler.probe ? '' : ' — no probe, nothing would notice a release'}`
                                            : 'no poll window'}>
                                            <Box sx={{ display: 'inline-flex' }}>
                                                <StageIcon state={schedulerState(r)} />
                                            </Box>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Tooltip
                                            title={
                                                [
                                                    eligibleCount(r) ? `not enrolled: ${(enrolment(r).eligible || []).join(', ')}` : '',
                                                    blockedCount(r) ? `blocked: ${(enrolment(r).blocked || []).map((b) => `${b.title} (${b.reason})`).join(', ')}` : '',
                                                    mismatchCount(r) ? `recorded in another state: ${(enrolment(r).state_mismatch || []).map((m) => `${m.title} (${m.recorded_state})`).join(', ')}` : '',
                                                ].filter(Boolean).join(' · ') || 'all districts in this state are enrolled'
                                            }
                                        >
                                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                                <Fraction have={enrolledCount(r)} total={enrolment(r).in_state || 0} />
                                                {blockedCount(r) > 0 && (
                                                    <Box component="span" sx={{ fontSize: 11, color: BAD }}>
                                                        +{blockedCount(r)}
                                                    </Box>
                                                )}
                                                {mismatchCount(r) > 0 && (
                                                    <Box component="span" sx={{ fontSize: 11, color: WARN }}>!</Box>
                                                )}
                                            </Box>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Fraction have={c.with_data || 0} total={c.clients || 0} />
                                    </TableCell>
                                    <TableCell align="center">
                                        <Fraction have={c.fully_registered || 0} total={c.clients || 0} />
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell sx={{ py: 0, borderBottom: isOpen ? undefined : 'none' }} colSpan={7}>
                                        <Collapse in={isOpen} timeout="auto" unmountOnExit>
                                            {/* unmountOnExit so a closed panel holds no stale
                                                enrolment list from before someone enrolled. */}
                                                            <EnrolmentPanel stateName={STATE_NAMES[r.abbrev]} />
                                        </Collapse>
                                    </TableCell>
                                </TableRow>
                            </React.Fragment>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            <Typography className="d-font" sx={{ fontSize: 11.5, color: '#888', mt: 1.5 }}>
                Click a state to see who is enrolled and to enrol a district.
                “Enrolled” counts districts recorded in that state (<code>NexusClient.State</code>);
                “Holding data” and “Registered” count only the enrolled ones, so the three read as a
                funnel. <b>+n</b> marks districts that cannot be enrolled until a storage container
                exists, and <b>!</b> marks a district receiving this state’s data while recorded in
                another — both are left as they are rather than reconciled, because enrolment keys on
                that field. “Registered” is measured against the combine’s own output list, not
                SharePoint’s count nor a raw blob count; either of those reports complete states as
                incomplete.
            </Typography>
        </Box>
    );
};

export default StateDataConsole;
